import ReactDOM from 'react-dom/client';
import './index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './styles/_base.scss';
import { BrowserRouter } from 'react-router-dom';
import store from './store/store';
import { Provider } from 'react-redux';
import './i18n';
import { initAnalytics, trackPageView, startEngagementTimer, flushEngagement, attachScrollDepthListener, tiktokIdentify, setTikTokIdentifyBarrier, customEventSender } from './utils/analytics';
import { getCurrentChannel } from './config';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </Provider>
);

// Early loader dismissal: hide as soon as React commits first frame
requestAnimationFrame(() => {
  const loader = document.getElementById('cosmic-loader');
  if (loader) {
    loader.classList.add('hidden');
    setTimeout(() => loader.remove(), 400);
  }
});

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Initialize analytics; send page_view immediately. Run TikTok identify fully in background.
if (initAnalytics()) {
  startEngagementTimer();
  let detachScroll = attachScrollDepthListener();

  // Track initial page immediately (don't wait for identify)
  trackPageView(window.location.pathname, document.title);

  // Track route changes
  window.addEventListener('popstate', () => trackPageView(window.location.pathname, document.title));

  // Flush engagement on unload
  window.addEventListener('beforeunload', () => {
    try { flushEngagement(); } catch (_) {}
    try { detachScroll && detachScroll(); } catch (_) {}
  });

  // Prepare TikTok identify barrier (guest IP) without blocking UI/GA
  const fetchClientIp = async (timeoutMs = 1200) => {
    try {
      const controller = new AbortController();
      const to = setTimeout(() => controller.abort(), timeoutMs);
      
      // Try multiple CORS-friendly IP services as fallbacks
      const services = [
        'https://api.ipify.org?format=text',
        'https://checkip.amazonaws.com',
        'https://icanhazip.com',
        'https://api.my-ip.io/ip'
      ];
      
      for (const service of services) {
        try {
          const res = await fetch(service, { signal: controller.signal });
          if (res.ok) {
            const text = await res.text();
            clearTimeout(to);
            return String(text || '').trim();
          }
        } catch (err) {
//          console.log(`IP service ${service} failed:`, err.message);
          continue;
        }
      }
      
      clearTimeout(to);
      return '';
    } catch (_) {
      return '';
    }
  };

  const initTikTokIdentify = (async () => {
    try {
      const ip = await fetchClientIp(1200);
      if (!ip) return;
      try {
        const key = 'ttq_identified_ip';
        const prev = sessionStorage.getItem(key);
        if (prev === ip) return; // dedupe across reloads
        sessionStorage.setItem(key, ip);
      } catch (_) { /* ignore storage issues */ }
      await tiktokIdentify({ external_id: ip });
    } catch (_) { /* ignore */ }
  })();

  // Set barrier so TikTok events queue behind identify, but GA/UI continue
  setTikTokIdentifyBarrier(initTikTokIdentify);
}

// Always notify backend analytics on LP load (even in localhost)
try {
  const channel = getCurrentChannel();
  const page_path = window.location.pathname;
  const page_title = document.title;
  const page_location = window.location.href;
  const referrer = document.referrer || '';
  const ua = navigator.userAgent || '';
  const lang = navigator.language || '';
  const debug_mode = /localhost|127\.0\.0\.1|0\.0\.0\.0/.test(window.location.hostname);
  customEventSender('page_view', { channel, page_path, page_title, page_location, referrer, ua, lang, debug_mode });
} catch (_) { /* noop */ }

// Always notify backend analytics on scroll depth milestones (25/50/75/100) once each
(function attachBackendScrollDepth() {
  try {
    const thresholds = [25, 50, 75, 100];
    const reached = new Set();
    const channel = getCurrentChannel();

    const getContainer = () => document.querySelector('.image-grid-container');

    const computeDepthPercent = () => {
      const container = getContainer();
      if (container && container.scrollHeight > container.clientHeight) {
        const y = container.scrollTop || 0;
        const maxScrollable = Math.max(container.scrollHeight - container.clientHeight, 1);
        return Math.min(100, Math.max(0, Math.round((y / maxScrollable) * 100)));
      }
      // fallback: window/document scrolling
      const y = window.scrollY || window.pageYOffset || 0;
      const docHeight = document.documentElement.scrollHeight || 0;
      const winHeight = window.innerHeight || 0;
      const maxScrollable = Math.max(docHeight - winHeight, 1);
      return Math.min(100, Math.max(0, Math.round((y / maxScrollable) * 100)));
    };

    const onScroll = () => {
      const depth = computeDepthPercent();
      thresholds.forEach((t) => {
        if (depth >= t && !reached.has(t)) {
          reached.add(t);
          customEventSender('scroll_depth', { channel, percent: t });
        }
      });
    };

    // Attach to both window and the grid container (if present)
    window.addEventListener('scroll', onScroll, { passive: true });
    const attachToContainerIfPresent = () => {
      const c = getContainer();
      if (c && !c.__backendScrollAttached) {
        c.addEventListener('scroll', onScroll, { passive: true });
        c.__backendScrollAttached = true;
      }
    };
    attachToContainerIfPresent();

    // Observe for late-mount of the container
    const observer = new MutationObserver(() => attachToContainerIfPresent());
    observer.observe(document.body, { childList: true, subtree: true });

    // Fire once in case the user loads mid-page
    onScroll();

    // Detach on unload
    window.addEventListener('beforeunload', () => {
      try { window.removeEventListener('scroll', onScroll); } catch (_) {}
      try {
        const c = getContainer();
        if (c) c.removeEventListener('scroll', onScroll);
      } catch (_) {}
      try { observer.disconnect(); } catch (_) {}
    });
  } catch (_) { /* noop */ }
})();
